import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Grid,
  Input,
  List,
  Table,
  Modal,
  Radio,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
  message,
  Spin
} from 'antd';
import {
  CalendarOutlined,
  CommentOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  LikeOutlined,
  MessageOutlined,
  PaperClipOutlined,
  PlusOutlined,
  UploadOutlined,
  FileOutlined,
  TeamOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  createBoardPost,
  searchBoardPosts,
  getBoardPost,
  updateBoardPost,
  deleteBoardPost,
  increaseViewCount,
  recommendBoardPost,
  createBoardPostComment,
  deleteBoardPostComment
} from '../api/boardApi';
import {
  getFileCategoryByCode,
  uploadFilesToCategory,
  uploadManagedFile,
  listManagedFiles,
  deleteManagedFile,
  deleteFileGroup,
  downloadManagedFileById,
  downloadFileByUrl,
} from '../api/fileManagementApi';
import useCurrentUser from '../hooks/useCurrentUser';

const { Title, Paragraph, Text } = Typography;

const PAGE_SIZE = 20;
const DEFAULT_CATEGORIES = ['공지', 'FAQ', '가이드', '자료실'];
const BOARD_CATEGORY_CODE = 'BOARD';

const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const display = value % 1 === 0 ? value : value.toFixed(1);
  return `${display} ${units[unitIndex]}`;
};

const normalizeCategory = (category) => (category && category.trim()) || '미분류';

const categoryColor = (category) => {
  switch (category) {
    case '공지':
      return 'volcano';
    case 'FAQ':
      return 'geekblue';
    case '가이드':
      return 'green';
    case '자료실':
      return 'purple';
    default:
      return 'blue';
  }
};

const tagColor = (tag) => {
  if (!tag) return 'default';
  const lowered = tag.toLowerCase();
  if (lowered.includes('시스템')) return 'red';
  if (lowered.includes('프로젝트')) return 'gold';
  if (lowered.includes('도메인')) return 'cyan';
  if (lowered.includes('용어')) return 'blue';
  return 'default';
};

const formatDate = (isoString) => {
  const d = dayjs(isoString);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : isoString;
};

const toFiniteNumber = (value, fallback = 0) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};

const extractCount = (detail, keys, fallback = 0) => {
  if (!detail) return fallback;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(detail, key)) {
      const numeric = toFiniteNumber(detail[key], fallback);
      if (numeric !== fallback) {
        return numeric;
      }
    }
  }
  return fallback;
};

const countCommentsRecursive = (comments) => {
  if (!Array.isArray(comments) || comments.length === 0) {
    return 0;
  }
  return comments.reduce((total, comment) => {
    const children = Array.isArray(comment?.replies) ? comment.replies : [];
    return total + 1 + countCommentsRecursive(children);
  }, 0);
};

const flattenComments = (comments, depth = 0, acc = []) => {
  if (!Array.isArray(comments) || comments.length === 0) {
    return acc;
  }
  comments.forEach((comment) => {
    acc.push({ comment, depth });
    if (Array.isArray(comment?.replies) && comment.replies.length > 0) {
      flattenComments(comment.replies, depth + 1, acc);
    }
  });
  return acc;
};

const normalizeCommentStatus = (status) => {
  if (typeof status !== 'string') {
    return '';
  }
  const trimmed = status.trim();
  return trimmed ? trimmed.toUpperCase() : '';
};

const COMMENT_STATUS_MESSAGES = Object.freeze({
  NORMAL: null,
  DELETED: '작성자가 삭제한 댓글입니다.',
  BLOCKED: '차단된 댓글입니다.',
  ADMIN_DELETED: '관리자에 의해 삭제된 댓글입니다.'
});

const COMMENT_STATUS_LABELS = Object.freeze({
  DELETED: '삭제됨',
  BLOCKED: '차단됨',
  ADMIN_DELETED: '관리자 삭제'
});

const getCommentDisplayContent = (comment) => {
  if (!comment) {
    return '';
  }
  const status = normalizeCommentStatus(comment.status);
  const fallback = typeof comment.content === 'string' ? comment.content : '';
  if (status && status !== 'NORMAL') {
    return COMMENT_STATUS_MESSAGES[status] || fallback || '처리된 댓글입니다.';
  }
  return fallback;
};

const isInteractiveComment = (comment) => {
  const status = normalizeCommentStatus(comment?.status);
  return !status || status === 'NORMAL';
};

const resolveCommentAuthor = (comment) => comment?.author || comment?.rgtr || '내부 사용자';

const getCommentCount = (detail) => {
  if (!detail) return 0;
  if (Array.isArray(detail.comments)) {
    return countCommentsRecursive(detail.comments);
  }
  return extractCount(detail, COMMENT_COUNT_KEYS, 0);
};

const VIEW_COUNT_KEYS = ['viewCnt', 'readCnt', 'hitCnt', 'viewCount', 'views', 'hitCount'];
const RECOMMEND_COUNT_KEYS = ['recommendCnt', 'recommendCount', 'likeCnt', 'likes', 'recomCnt', 'upvoteCount'];
const COMMENT_COUNT_KEYS = ['commentCount', 'commentsCount', 'commentCnt', 'cmntCnt', 'totalCommentCount'];

const CommentComposer = React.memo(({
  postId,
  currentUserName,
  isReplying,
  replyTarget,
  onCancelReply,
  onSubmit,
  submitting,
}) => {
  const [form] = Form.useForm();
  const contentValue = Form.useWatch('content', form);

  useEffect(() => {
    form.resetFields();
  }, [form, postId]);

  const handleFinish = useCallback((values) => {
    onSubmit(postId, values, form);
  }, [form, onSubmit, postId]);

  const trimmedContent = typeof contentValue === 'string' ? contentValue.trim() : '';

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish}>
      {isReplying && (
        <Alert
          type="info"
          showIcon
          message={`${replyTarget?.author || '내부 사용자'} 님에게 답글 작성 중`}
          description={replyTarget?.content ? (
            <Text type="secondary">
              {replyTarget.content.length > 80 ? `${replyTarget.content.slice(0, 80)}…` : replyTarget.content}
            </Text>
          ) : null}
          action={(
            <Button size="small" type="link" onClick={onCancelReply}>
              취소
            </Button>
          )}
          style={{ marginBottom: 12 }}
        />
      )}
      <Form.Item
        name="content"
        label="댓글"
        rules={[{ required: true, message: '댓글을 입력하세요.' }]}
      >
        <Input.TextArea rows={3} placeholder="댓글을 입력하세요" autoSize={{ minRows: 3, maxRows: 6 }} />
      </Form.Item>
      <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          disabled={submitting || !trimmedContent}
        >
          {isReplying ? '답글 등록' : '댓글 등록'}
        </Button>
      </Space>
    </Form>
  );
});

CommentComposer.displayName = 'CommentComposer';

function BoardPage() {
  const { user: currentUser } = useCurrentUser();
  const screens = Grid.useBreakpoint();
  const [postForm] = Form.useForm();
  const [posts, setPosts] = useState([]);
  const [postDetails, setPostDetails] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingPostId, setEditingPostId] = useState(null);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingPost, setSavingPost] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [categoryStats, setCategoryStats] = useState({});
  const [replyTarget, setReplyTarget] = useState(null);
  const [boardFileCategory, setBoardFileCategory] = useState(null);
  const [boardCategoryLoading, setBoardCategoryLoading] = useState(false);
  const [attachmentGroupId, setAttachmentGroupId] = useState(null);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState(() => new Set());
  const [downloadingAttachments, setDownloadingAttachments] = useState({});

  const boardPostFetcherRef = useRef(getBoardPost);
  const attachmentInputRef = useRef(null);

  useEffect(() => {
    boardPostFetcherRef.current = getBoardPost;
  }, []);

  const fetchBoardFileCategory = useCallback(async () => {
    setBoardCategoryLoading(true);
    try {
      const response = await getFileCategoryByCode(BOARD_CATEGORY_CODE);
      const category = response?.data ?? response;
      setBoardFileCategory(category);
    } catch (err) {
      console.warn('BOARD 카테고리 조회 실패:', err);
      message.warning('게시판 첨부 파일 카테고리를 불러오지 못했습니다.');
    } finally {
      setBoardCategoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoardFileCategory();
  }, [fetchBoardFileCategory]);

  const loadAttachments = useCallback(async (groupId) => {
    if (!groupId) {
      return [];
    }
    try {
      const files = await listManagedFiles(groupId);
      return Array.isArray(files) ? files : [];
    } catch (err) {
      console.warn('첨부 파일 조회 실패:', err);
      return [];
    }
  }, []);

  const currentUserName = useMemo(
    () => currentUser?.userNm || currentUser?.lgnUserNm || currentUser?.loginId || '',
    [currentUser]
  );

  const currentUserId = useMemo(
    () => currentUser?.userId,
    [currentUser]
  );

  // 현재 사용자가 해당 항목의 작성자인지 확인
  const isOwner = useCallback((item) => {
    if (!currentUserId || !item?.rgtrId) {
      return false;
    }
    return currentUserId === item.rgtrId;
  }, [currentUserId]);

  const loadPosts = useCallback(async (pageOverride) => {
    const targetPage = typeof pageOverride === 'number' ? pageOverride : page;
    setLoading(true);
    try {
      const params = {
        keyword: keyword.trim() || undefined,
        category: categoryFilter === 'ALL' ? undefined : categoryFilter,
        sort: sortOrder === 'asc' ? 'ASC' : 'DESC',
        page: targetPage,
        size: PAGE_SIZE
      };
      const response = await searchBoardPosts(params);
      const data = response?.data ?? response;
      const list = Array.isArray(data?.content) ? data.content : [];
      setPosts(list);
      setTotalElements(Number.isFinite(data?.totalElements) ? data.totalElements : 0);
      setCategoryStats(typeof data?.categoryCounts === 'object' && data.categoryCounts !== null ? data.categoryCounts : {});
      setPostDetails((prev) => {
        if (!Array.isArray(list) || list.length === 0) {
          return prev;
        }
        const next = { ...prev };
        list.forEach((item) => {
          const postId = item.boardPostId || item.id;
          if (!postId) {
            return;
          }
          const prevEntry = next[postId] || {};
          const detailLoaded = prevEntry.__detailLoaded === true;
          const merged = {
            ...prevEntry,
            ...item
          };
          if (detailLoaded) {
            merged.__detailLoaded = true;
            merged.comments = prevEntry.comments;
            if (Array.isArray(prevEntry.attachments)) {
              merged.attachments = prevEntry.attachments;
            }
          }
          next[postId] = merged;
        });
        return next;
      });
      if (typeof pageOverride === 'number' && pageOverride !== page) {
        setPage(pageOverride);
      }
    } catch (err) {
      console.warn('게시판 데이터 조회 실패:', err);
      message.error('게시글을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [keyword, categoryFilter, sortOrder, page]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const fetchPostDetail = useCallback(async (postId, options = {}) => {
    if (!postId) return null;
    const force = options.force ?? false;
    const cached = postDetails[postId];
    if (!force && cached?.__detailLoaded) {
      return cached;
    }
    // 로딩 표시 제거
    try {
  const request = boardPostFetcherRef.current;
  const response = await request(postId);
      const detail = response?.data ?? response;
      if (detail) {
        const attachments = detail.fileGroupId ? await loadAttachments(detail.fileGroupId) : [];
        setPostDetails((prev) => ({
          ...prev,
          [postId]: {
            ...(prev[postId] || {}),
            ...detail,
            attachments,
            __detailLoaded: true
          }
        }));
      }
      return detail;
    } catch (err) {
      console.warn('게시글 상세 조회 실패:', err);
      message.error('게시글 상세를 불러오지 못했습니다.');
      throw err;
    }
  }, [postDetails, loadAttachments]);

  const categories = useMemo(() => {
    const defaults = DEFAULT_CATEGORIES.map(normalizeCategory);
    const dynamicSet = new Set();

    Object.entries(categoryStats || {})
      .filter(([key]) => key !== '전체')
      .forEach(([key]) => {
        const normalized = normalizeCategory(key);
        if (!defaults.includes(normalized)) {
          dynamicSet.add(normalized);
        }
      });

    if (dynamicSet.size === 0 && posts.length) {
      posts.forEach((post) => {
        const normalized = normalizeCategory(post.category);
        if (!defaults.includes(normalized)) {
          dynamicSet.add(normalized);
        }
      });
    }

    const ordered = [];
    const seen = new Set();
    defaults.forEach((cat) => {
      if (!seen.has(cat)) {
        ordered.push(cat);
        seen.add(cat);
      }
    });

    Array.from(dynamicSet)
      .sort((a, b) => a.localeCompare(b, 'ko'))
      .forEach((cat) => {
        if (!seen.has(cat)) {
          ordered.push(cat);
          seen.add(cat);
        }
      });

    if (!ordered.length && posts.length) {
      posts.forEach((post) => {
        const normalized = normalizeCategory(post.category);
        if (!seen.has(normalized)) {
          ordered.push(normalized);
          seen.add(normalized);
        }
      });
    }

    return ordered.length ? ordered : defaults;
  }, [categoryStats, posts]);

  const stats = useMemo(() => {
    const result = [];
    const total = categoryStats?.전체 ?? totalElements ?? posts.length;
    result.push(['전체', total]);
    categories.forEach((cat) => {
      result.push([cat, categoryStats?.[cat] ?? 0]);
    });
    return result;
  }, [categoryStats, categories, totalElements, posts.length]);

  const resetAttachmentState = useCallback(() => {
    setAttachmentGroupId(null);
    setExistingAttachments([]);
    setPendingAttachments([]);
    setAttachmentsToDelete(() => new Set());
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  }, [attachmentInputRef]);

  const resetPostForm = useCallback(() => {
    postForm.resetFields();
    resetAttachmentState();
    const initialCategory = categories[0] || DEFAULT_CATEGORIES[0] || '공지';
    postForm.setFieldsValue({
      category: initialCategory
    });
  }, [categories, postForm, resetAttachmentState]);

  useEffect(() => {
    if (!isPostModalOpen && modalMode === 'create') {
      const initialCategory = categories[0] || DEFAULT_CATEGORIES[0] || '공지';
      postForm.setFieldsValue({ category: initialCategory });
    }
  }, [categories, isPostModalOpen, modalMode, postForm]);

  const openCreateModal = () => {
    setModalMode('create');
    setEditingPostId(null);
    resetPostForm();
    setIsPostModalOpen(true);
  };

  const openEditModal = async (postId) => {
    if (!postId) return;
    let detail = postDetails[postId];
    if (!detail || !detail.content) {
      try {
        detail = (await fetchPostDetail(postId)) || postDetails[postId];
      } catch (err) {
        console.warn('게시글 편집 데이터 조회 실패:', err);
        return;
      }
    }
    if (!detail) {
      message.error('게시글 정보를 찾을 수 없습니다.');
      return;
    }
    resetAttachmentState();
    const normalizedCategory = normalizeCategory(detail.category);
    postForm.setFieldsValue({
      title: detail.title,
      content: detail.content,
      tags: Array.isArray(detail.tags) ? detail.tags.join(', ') : detail.tags,
      category: normalizedCategory
    });
    const groupId = detail.fileGroupId || null;
    setAttachmentGroupId(groupId);
    const attachmentList = Array.isArray(detail.attachments)
      ? detail.attachments
      : await loadAttachments(groupId);
    setExistingAttachments(Array.isArray(attachmentList) ? attachmentList : []);
    setPendingAttachments([]);
    setAttachmentsToDelete(() => new Set());
    setModalMode('edit');
    setEditingPostId(postId);
    setIsPostModalOpen(true);
  };

  const closePostModal = () => {
    setIsPostModalOpen(false);
    setEditingPostId(null);
    setModalMode('create');
    resetPostForm();
  };

  const handleSubmitPost = async () => {
    let createdGroupId = null;
    try {
      const values = await postForm.validateFields();
      const category = normalizeCategory(values.category);
      if (!category) {
        message.warning('카테고리를 선택해주세요.');
        return;
      }
      const tags = Array.isArray(values.tags)
        ? values.tags.map((tag) => tag.trim()).filter(Boolean)
        : (values.tags || '')
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);

      const pendingFiles = pendingAttachments.map((item) => item.file).filter(Boolean);
      const markedForDeletion = Array.from(attachmentsToDelete);
      const remainingExisting = existingAttachments.filter((file) => !attachmentsToDelete.has(file.fileId));
      const previousGroupId = attachmentGroupId || null;
      let targetGroupId = previousGroupId;
      const willHaveAttachments = pendingFiles.length > 0 || remainingExisting.length > 0;

      if (pendingFiles.length > 0) {
        const uploadId = `board-${Date.now()}`;
        if (targetGroupId) {
          const formData = new FormData();
          formData.append('uploadId', uploadId);
          pendingFiles.forEach((file, index) => {
            const filename = file?.name ? file.name : `file-${index}`;
            formData.append('files', file, filename);
          });
          await uploadManagedFile(targetGroupId, formData);
        } else {
          if (!boardFileCategory?.fileCategoryId) {
            message.error('게시판 첨부 파일 카테고리를 찾을 수 없습니다.');
            return;
          }
          const uploadResult = await uploadFilesToCategory(
            boardFileCategory.fileCategoryId,
            pendingFiles,
            { uploadId }
          );
          targetGroupId = uploadResult?.groupId || null;
          createdGroupId = targetGroupId;
        }
      }

      if (willHaveAttachments && !targetGroupId) {
        throw new Error('첨부 파일 그룹 생성에 실패했습니다.');
      }

      const payload = {
        title: values.title.trim(),
        content: values.content.trim(),
        category,
        tags,
        fileGroupId: willHaveAttachments ? targetGroupId : null
      };

      setSavingPost(true);

      if (modalMode === 'edit' && editingPostId) {
        const response = await updateBoardPost(editingPostId, payload);
        message.success(response?.message || '게시글이 수정되었습니다.');

        if (markedForDeletion.length > 0 && (payload.fileGroupId || previousGroupId)) {
          const groupForDeletion = payload.fileGroupId || previousGroupId;
          for (const fileId of markedForDeletion) {
            try {
              await deleteManagedFile(fileId);
            } catch (error) {
              console.warn('첨부 파일 삭제 실패:', error);
            }
          }
          if (!willHaveAttachments && groupForDeletion) {
            try {
              await deleteFileGroup(groupForDeletion);
            } catch (error) {
              console.warn('첨부 파일 그룹 삭제 실패:', error);
            }
          }
        } else if (!willHaveAttachments && previousGroupId) {
          try {
            await deleteFileGroup(previousGroupId);
          } catch (error) {
            console.warn('첨부 파일 그룹 삭제 실패:', error);
          }
        }

        await loadPosts(page);
        await fetchPostDetail(editingPostId, { force: true });
      } else {
        const response = await createBoardPost(payload);
        message.success(response?.message || '새 글이 등록되었습니다.');
        await loadPosts(0);
        setPage(0);
      }

      resetAttachmentState();
      closePostModal();
    } catch (err) {
      if (createdGroupId) {
        try {
          await deleteFileGroup(createdGroupId);
        } catch (cleanupError) {
          console.warn('임시 파일 그룹 삭제 실패:', cleanupError);
        }
      }
      if (err?.errorFields) {
        return;
      }
      console.warn('게시글 저장 실패:', err);
      message.error(err?.message || '게시글을 저장하지 못했습니다.');
    } finally {
      setSavingPost(false);
    }
  };

  const handleTogglePost = useCallback(async (post, forceExpand) => {
    if (!post) return;
    const postId = post.boardPostId || post.id;
    if (!postId) return;
    const isCurrentlyExpanded = expandedPostId === postId;
    const shouldExpand = typeof forceExpand === 'boolean' ? forceExpand : !isCurrentlyExpanded;

    if (!shouldExpand) {
      if (isCurrentlyExpanded) {
        setExpandedPostId(null);
      }
      setReplyTarget(null);
      return;
    }

    setExpandedPostId(postId);
    setReplyTarget(null);
    try {
      try {
        await increaseViewCount(postId);
      } catch (viewError) {
        console.warn('조회수 증가 실패:', viewError);
      }
      await fetchPostDetail(postId);
    } catch (err) {
      setExpandedPostId(null);
    }
  }, [expandedPostId, fetchPostDetail]);

  const handleDeletePost = (postId) => {
    if (!postId) return;
    Modal.confirm({
      title: '게시글을 삭제하시겠습니까?',
      content: '삭제 후에는 되돌릴 수 없습니다.',
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      async onOk() {
        try {
          const response = await deleteBoardPost(postId);
          message.success(response?.message || '게시글이 삭제되었습니다.');
          setPostDetails((prev) => {
            const next = { ...prev };
            delete next[postId];
            return next;
          });
          if (expandedPostId === postId) {
            setExpandedPostId(null);
          }
          await loadPosts(page);
        } catch (err) {
          console.warn('게시글 삭제 실패:', err);
        }
      }
    });
  };

  const handleStartReply = (postId, comment) => {
    if (!postId || !comment) return;
    const commentId = comment.boardPostCommentId || comment.commentId;
    if (!commentId) return;
    if (!isInteractiveComment(comment)) return;
    setReplyTarget({
      postId,
      commentId,
      author: resolveCommentAuthor(comment),
      content: getCommentDisplayContent(comment)
    });
  };

  const handleSubmitComment = async (postId, values, formInstance) => {
    if (!postId) return;
    try {
      const rawContent = typeof values === 'string' ? values : values?.content;
      const content = (rawContent || '').trim();
      if (!content) {
        message.warning('댓글 내용을 입력하세요.');
        return;
      }
      const payload = {
        content
      };
      if (replyTarget?.postId === postId && replyTarget?.commentId) {
        payload.parentCommentId = replyTarget.commentId;
      }
      setCommentSubmitting(true);
      const response = await createBoardPostComment(postId, payload);
      await fetchPostDetail(postId, { force: true });
      if (formInstance) {
        formInstance.resetFields();
      }
      if (replyTarget?.postId === postId) {
        setReplyTarget(null);
      }
      message.success(response?.message || '댓글이 등록되었습니다.');
    } catch (err) {
      console.warn('댓글 등록 실패:', err);
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDeleteComment = (postId, commentId) => {
    if (!postId || !commentId) return;
    Modal.confirm({
      title: '댓글을 삭제하시겠습니까?',
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      async onOk() {
        try {
          const response = await deleteBoardPostComment(postId, commentId);
          await fetchPostDetail(postId, { force: true });
          setReplyTarget((prev) => (prev?.postId === postId ? null : prev));
          message.success(response?.message || '댓글이 삭제되었습니다.');
        } catch (err) {
          console.warn('댓글 삭제 실패:', err);
        }
      }
    });
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchInput(value);
    if (value === '') {
      setKeyword('');
      setPage(0);
    }
  };

  const handleSearchSubmit = (value) => {
    const trimmed = (value ?? '').trim();
    setKeyword(trimmed);
    setSearchInput(trimmed);
    setPage(0);
  };

  const handleOpenFilePicker = useCallback(() => {
    if (!attachmentInputRef.current) {
      return;
    }
    if (!attachmentGroupId && !boardFileCategory?.fileCategoryId) {
      if (!boardCategoryLoading) {
        message.warning('게시판 첨부 파일 카테고리를 불러오는 중입니다. 잠시만 기다려주세요.');
      }
      return;
    }
    attachmentInputRef.current.click();
  }, [attachmentGroupId, boardCategoryLoading, boardFileCategory]);

  const handleAttachmentSelection = useCallback((event) => {
    const files = Array.from(event?.target?.files || []);
    if (!files.length) {
      return;
    }
    setPendingAttachments((prev) => {
      const existingSignatures = new Set(prev.map((item) => item.signature));
      const additions = [];
      files.forEach((file) => {
        const signature = `${file.name}-${file.size}-${file.lastModified}`;
        if (!existingSignatures.has(signature)) {
          additions.push({
            uid: `${signature}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            file,
            signature,
          });
          existingSignatures.add(signature);
        }
      });
      return additions.length ? [...prev, ...additions] : prev;
    });
    if (event?.target) {
      event.target.value = '';
    }
  }, []);

  const removePendingAttachment = useCallback((uid) => {
    setPendingAttachments((prev) => prev.filter((item) => item.uid !== uid));
  }, []);

  const toggleAttachmentDeletion = useCallback((fileId) => {
    setAttachmentsToDelete((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const handleDownloadAttachment = useCallback(async (file) => {
    if (!file) {
      return;
    }
    const downloadKey = file.fileId || file.downloadUrl;
    if (!downloadKey) {
      message.warning('다운로드 가능한 파일 정보를 찾지 못했습니다.');
      return;
    }
    setDownloadingAttachments((prev) => {
      if (prev[downloadKey]) {
        return prev;
      }
      return { ...prev, [downloadKey]: true };
    });
    try {
      let result = null;
      if (file.fileId) {
        result = await downloadManagedFileById(file.fileId);
      } else if (file.downloadUrl) {
        result = await downloadFileByUrl(file.downloadUrl);
      }
      const blob = result?.blob;
      if (!blob) {
        throw new Error('다운로드 데이터가 비어 있습니다.');
      }
      const suggested = result?.fileName || file.originalFileName || 'download';
      if (typeof window === 'undefined' || !window.URL || typeof window.URL.createObjectURL !== 'function') {
        throw new Error('브라우저에서 파일 다운로드 생성을 지원하지 않습니다.');
      }
      if (typeof document === 'undefined') {
        throw new Error('문서 객체를 찾을 수 없습니다.');
      }
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = suggested;
      document.body.appendChild(anchor);
      anchor.click();
      setTimeout(() => {
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(objectUrl);
      }, 0);
    } catch (error) {
      console.warn('첨부 파일 다운로드 실패:', error);
      message.error('첨부 파일을 다운로드하지 못했습니다.');
    } finally {
      setDownloadingAttachments((prev) => {
        if (!prev[downloadKey]) {
          return prev;
        }
        const next = { ...prev };
        delete next[downloadKey];
        return next;
      });
    }
  }, [downloadFileByUrl, downloadManagedFileById, message]);

  const visiblePosts = posts;
  const expandedRowKeys = useMemo(() => (expandedPostId ? [expandedPostId] : []), [expandedPostId]);

  const handleExpandChange = useCallback((expanded, record) => {
    handleTogglePost(record, expanded);
  }, [handleTogglePost]);

  const columns = useMemo(() => {
    const isMobile = !screens.md;
    
    return [
    {
      title: '구분',
      dataIndex: 'category',
      key: 'category',
      width: isMobile ? 80 : 120,
      responsive: isMobile ? undefined : ['md'],
      sorter: (a, b) => {
        const catA = normalizeCategory((postDetails[a.boardPostId || a.id] || a).category);
        const catB = normalizeCategory((postDetails[b.boardPostId || b.id] || b).category);
        return catA.localeCompare(catB);
      },
      render: (_, record) => {
        const postId = record.boardPostId || record.id;
        const detail = postDetails[postId] || record;
        const normalized = normalizeCategory(detail.category);
        return (
          <Tag color={categoryColor(normalized)}>
            {normalized}
          </Tag>
        );
      }
    },
    {
      title: '제목',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      sorter: (a, b) => {
        const titleA = (postDetails[a.boardPostId || a.id] || a).title || '';
        const titleB = (postDetails[b.boardPostId || b.id] || b).title || '';
        return titleA.localeCompare(titleB);
      },
      render: (_, record) => {
        const postId = record.boardPostId || record.id;
        const detail = postDetails[postId] || record;
        const detailCommentCount = getCommentCount(detail);
        const fallbackCommentCount = extractCount(record, COMMENT_COUNT_KEYS, 0);
        const commentCount = Math.max(detailCommentCount, fallbackCommentCount);
        const isExpanded = expandedPostId === postId;
        return (
          <Button
            type="link"
            onClick={() => handleTogglePost(record)}
            style={{ padding: 0, fontWeight: isExpanded ? 600 : 500, textAlign: 'left', whiteSpace: isMobile ? 'normal' : 'nowrap' }}
          >
            <Space size={6} align="center" wrap={isMobile}>
              <span style={{ color: '#1f1f1f' }}>{detail.title}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1890ff' }}>
                <span>[</span>
                <CommentOutlined />
                <span>{commentCount}</span>
                <span>]</span>
              </span>
            </Space>
          </Button>
        );
      }
    },
    {
      title: '작성자',
      dataIndex: 'author',
      key: 'author',
      width: isMobile ? 80 : 160,
      responsive: ['sm'],
      sorter: (a, b) => {
        const authorA = (postDetails[a.boardPostId || a.id] || a).userNm || (postDetails[a.boardPostId || a.id] || a).author || (postDetails[a.boardPostId || a.id] || a).rgtr || '내부 사용자';
        const authorB = (postDetails[b.boardPostId || b.id] || b).userNm || (postDetails[b.boardPostId || b.id] || b).author || (postDetails[b.boardPostId || b.id] || b).rgtr || '내부 사용자';
        return authorA.localeCompare(authorB);
      },
      render: (_, record) => {
        const postId = record.boardPostId || record.id;
        const detail = postDetails[postId] || record;
        return detail.userNm || detail.author || detail.rgtr || '내부 사용자';
      }
    },
    {
      title: '작성일',
      dataIndex: 'regDt',
      key: 'regDt',
      width: isMobile ? 100 : 180,
      responsive: ['lg'],
      sorter: (a, b) => {
        const dateA = (postDetails[a.boardPostId || a.id] || a).regDt || (postDetails[a.boardPostId || a.id] || a).createdAt || '';
        const dateB = (postDetails[b.boardPostId || b.id] || b).regDt || (postDetails[b.boardPostId || b.id] || b).createdAt || '';
        return dateA.localeCompare(dateB);
      },
      render: (_, record) => {
        const postId = record.boardPostId || record.id;
        const detail = postDetails[postId] || record;
        return formatDate(detail.regDt || detail.createdAt);
      }
    },
    {
      title: '조회',
      dataIndex: 'viewCnt',
      key: 'viewCnt',
      align: 'right',
      width: isMobile ? 60 : 100,
      responsive: ['md'],
      sorter: (a, b) => {
        const viewA = extractCount((postDetails[a.boardPostId || a.id] || a), VIEW_COUNT_KEYS, 0);
        const viewB = extractCount((postDetails[b.boardPostId || b.id] || b), VIEW_COUNT_KEYS, 0);
        return viewA - viewB;
      },
      render: (_, record) => {
        const postId = record.boardPostId || record.id;
        const detail = postDetails[postId] || record;
        const fallback = extractCount(record, VIEW_COUNT_KEYS, 0);
        const viewCount = extractCount(detail, VIEW_COUNT_KEYS, fallback);
        return toFiniteNumber(viewCount, 0).toLocaleString('ko-KR');
      }
    },
    {
      title: '추천',
      dataIndex: 'recommendCnt',
      key: 'recommendCnt',
      align: 'right',
      width: isMobile ? 60 : 100,
      responsive: ['lg'],
      sorter: (a, b) => {
        const recA = extractCount((postDetails[a.boardPostId || a.id] || a), RECOMMEND_COUNT_KEYS, 0);
        const recB = extractCount((postDetails[b.boardPostId || b.id] || b), RECOMMEND_COUNT_KEYS, 0);
        return recA - recB;
      },
      render: (_, record) => {
        const postId = record.boardPostId || record.id;
        const detail = postDetails[postId] || record;
        const fallback = extractCount(record, RECOMMEND_COUNT_KEYS, 0);
        const recommendCount = extractCount(detail, RECOMMEND_COUNT_KEYS, fallback);
        return toFiniteNumber(recommendCount, 0).toLocaleString('ko-KR');
      }
    }
  ];}, [expandedPostId, handleTogglePost, postDetails, screens.md]);

  const expandedRowRender = (record) => {
    const postId = record.boardPostId || record.id;
    const detail = postDetails[postId] || record;
    const comments = Array.isArray(detail?.comments) ? detail.comments : [];
    const fallbackCommentCount = extractCount(record, COMMENT_COUNT_KEYS, 0);
    const commentCount = Math.max(getCommentCount(detail), fallbackCommentCount);
    const flattenedComments = flattenComments(comments);
    const isReplyingToCurrentPost = replyTarget?.postId === postId;
    const isDetailLoading = detailLoading && detailLoadingId === postId;

    if (isDetailLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <Spin />
        </div>
      );
    }

    return (
      <div style={{ padding: '16px 8px 8px' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <Space size={12} style={{ color: '#8c8c8c', fontSize: 12 }} wrap>
              <span><TeamOutlined /> {detail.userNm || detail.author || detail.rgtr || '내부 사용자'}</span>
              <span><CalendarOutlined /> {formatDate(detail.regDt || detail.createdAt)}</span>
            </Space>
            {isOwner(detail) && (
              <Space size={8}>
                <Button 
                  size="small"
                  icon={<EditOutlined />} 
                  onClick={() => openEditModal(postId)}
                >
                  수정
                </Button>
                <Button 
                  size="small"
                  danger 
                  icon={<DeleteOutlined />} 
                  onClick={() => handleDeletePost(postId)}
                >
                  삭제
                </Button>
              </Space>
            )}
          </div>
          {(detail.tags || []).length > 0 && (
            <Space wrap size={[8, 4]}>
              {(detail.tags || []).map((tag) => (
                <Tag key={tag} color={tagColor(tag)}>{`#${tag}`}</Tag>
              ))}
            </Space>
          )}
          <Paragraph style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>{detail.content}</Paragraph>
          {Array.isArray(detail.attachments) && detail.attachments.length > 0 && (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Text strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <PaperClipOutlined /> 첨부 파일 ({detail.attachments.length})
              </Text>
              <List
                size="small"
                dataSource={detail.attachments}
                locale={{ emptyText: null }}
                renderItem={(file) => {
                  const downloadKey = file.fileId || file.downloadUrl;
                  const downloading = downloadKey ? !!downloadingAttachments[downloadKey] : false;
                  const canDownload = !!downloadKey;
                  return (
                    <List.Item
                      key={downloadKey || file.originalFileName}
                      actions={canDownload ? [
                        <Button
                          key="download"
                          type="link"
                          size="small"
                          icon={<DownloadOutlined />}
                          loading={downloading}
                          onClick={() => handleDownloadAttachment(file)}
                        >
                          다운로드
                        </Button>
                      ] : []}
                    >
                      <Space size={8} wrap>
                        <FileOutlined />
                        {canDownload ? (
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, height: 'auto' }}
                            onClick={() => handleDownloadAttachment(file)}
                            loading={downloading}
                          >
                            {file.originalFileName}
                          </Button>
                        ) : (
                          <span>{file.originalFileName}</span>
                        )}
                        <Text type="secondary">{formatFileSize(file.fileSize)}</Text>
                        {file.uploadedAt && (
                          <Text type="secondary">{formatDate(file.uploadedAt)}</Text>
                        )}
                      </Space>
                    </List.Item>
                  );
                }}
              />
            </Space>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
            <Button
              type={detail.recommended ? "default" : "primary"}
              icon={<LikeOutlined />}
              disabled={isOwner(detail)}
              onClick={async () => {
                try {
                  await recommendBoardPost(postId);
                  const action = detail.recommended ? '추천을 취소했습니다.' : '추천했습니다!';
                  message.success(action);
                  await fetchPostDetail(postId);
                  await loadPosts(page);
                } catch (err) {
                  message.error(err?.response?.data?.message || '작업에 실패했습니다.');
                }
              }}
            >
              {detail.recommended ? '추천 취소' : '추천'} ({extractCount(detail, RECOMMEND_COUNT_KEYS, 0)})
            </Button>
          </div>
          <Divider style={{ margin: '12px 0' }} />
          <Title level={5} style={{ marginBottom: 0 }}>댓글 {commentCount}</Title>
          <List
            dataSource={flattenedComments}
            locale={{ emptyText: '등록된 댓글이 없습니다.' }}
            renderItem={({ comment, depth }, index) => {
              const commentId = comment.boardPostCommentId || comment.commentId;
              const status = normalizeCommentStatus(comment?.status);
              const interactive = isInteractiveComment(comment);
              const displayContent = getCommentDisplayContent(comment);
              const canModify = isOwner(comment);
              const actions = [];
              if (commentId && interactive) {
                actions.push(
                  <Button
                    key="reply"
                    type="link"
                    size="small"
                    icon={<CommentOutlined />}
                    onClick={() => handleStartReply(postId, comment)}
                  >
                    답글
                  </Button>
                );
                if (canModify) {
                  actions.push(
                    <Button
                      key="delete"
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteComment(postId, commentId)}
                    >
                      삭제
                    </Button>
                  );
                }
              }
              return (
                <List.Item
                  key={commentId || `${postId}-comment-${depth}-${index}`}
                  style={{ padding: '8px 0', paddingLeft: depth * 24, background: depth ? '#fafafa' : undefined }}
                  actions={actions}
                >
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space size={8} style={{ fontSize: 12, color: '#8c8c8c' }} wrap>
                      <span><TeamOutlined /> {resolveCommentAuthor(comment)}</span>
                      <span><CalendarOutlined /> {formatDate(comment.regDt || comment.createdAt)}</span>
                      {status && status !== 'NORMAL' && (
                        <Tag color="default" bordered={false} style={{ marginLeft: 0 }}>
                          {COMMENT_STATUS_LABELS[status] || status}
                        </Tag>
                      )}
                    </Space>
                    <Text type={interactive ? undefined : 'secondary'} italic={!interactive}>
                      {displayContent || '처리된 댓글입니다.'}
                    </Text>
                  </Space>
                </List.Item>
              );
            }}
          />
          <CommentComposer
            postId={postId}
            currentUserName={currentUserName}
            isReplying={isReplyingToCurrentPost}
            replyTarget={isReplyingToCurrentPost ? replyTarget : null}
            onCancelReply={() => setReplyTarget(null)}
            onSubmit={handleSubmitComment}
            submitting={commentSubmitting}
          />
        </Space>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%', padding: screens.xs ? '4px 8px 20px' : '8px 0 40px' }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Title level={screens.xs ? 4 : 2} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, fontSize: screens.xs ? '18px' : undefined }}>
            <MessageOutlined /> 게시판
          </Title>
          <Paragraph style={{ color: '#595959', marginBottom: 0, fontSize: screens.xs ? '13px' : undefined }}>
            공지, 자주 묻는 질문, 업무 가이드 등 메타데이터 관리와 관련된 최신 소식을 공유합니다. 필요한 내용을 검색하고, 새 글을 등록하여 팀과 정보를 나눌 수 있습니다.
          </Paragraph>
        </div>

        <Row gutter={[16, 16]}>
          {stats.map(([label, value]) => (
            <Col key={label} xs={12} sm={8} md={6} lg={6} xl={4}>
              <Card size="small" variant="outlined">
                <Statistic title={`${label} 게시물`} value={value} suffix="건" valueStyle={{ fontSize: 20 }} />
              </Card>
            </Col>
          ))}
        </Row>

        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space wrap align="center" size={[8, 8]} style={{ width: '100%' }}>
              <Input.Search
                style={{ minWidth: screens.xs ? 'auto' : 240, width: screens.xs ? '100%' : 'auto' }}
                value={searchInput}
                placeholder={screens.xs ? "검색" : "제목, 내용, 작성자, 태그 검색"}
                allowClear
                onChange={handleSearchChange}
                onSearch={handleSearchSubmit}
              />
              <Radio.Group 
                value={categoryFilter} 
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(0);
                }}
                size={screens.xs ? 'small' : 'middle'}
              >
                <Radio.Button value="ALL">전체</Radio.Button>
                {categories.map((cat) => (
                  <Radio.Button key={cat} value={cat}>
                    {cat}
                  </Radio.Button>
                ))}
              </Radio.Group>
              <Button 
                onClick={() => {
                  setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
                  setPage(0);
                }}
                size={screens.xs ? 'small' : 'middle'}
              >
                {sortOrder === 'desc' ? '최신순' : '오래된순'} 보기
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
                data-testid="open-create-modal"
                size={screens.xs ? 'small' : 'middle'}
              >
                {screens.xs ? '작성' : '새 글 작성'}
              </Button>
            </Space>

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ overflowX: 'auto' }}>
              <Table
                data-testid="board-table"
                columns={columns}
                dataSource={visiblePosts}
                rowKey={(post) => post.boardPostId || post.id}
                loading={loading}
                pagination={{
                  current: page + 1,
                  pageSize: PAGE_SIZE,
                  total: totalElements,
                  showSizeChanger: false,
                  onChange: (nextPage) => {
                    setExpandedPostId(null);
                    setPage(nextPage - 1);
                  },
                  size: screens.xs ? 'small' : 'default'
                }}
                locale={{
                  emptyText: <Empty description="등록된 게시물이 없습니다" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                }}
                expandable={{
                  expandedRowRender,
                  expandedRowKeys,
                  onExpand: handleExpandChange,
                  expandIcon: () => null,
                  columnWidth: 0
                }}
                size={screens.xs ? 'small' : 'middle'}
                scroll={{ x: 'max-content' }}
              />
            </div>
          </Space>
        </Card>
      </Space>

      <Modal
        title={modalMode === 'edit' ? '게시물 수정' : '새 게시물 작성'}
        open={isPostModalOpen}
        onOk={handleSubmitPost}
        okText={modalMode === 'edit' ? '수정' : '등록'}
        cancelText="취소"
        confirmLoading={savingPost}
        onCancel={closePostModal}
        destroyOnHidden
        maskClosable={false}
      >
        <Form form={postForm} layout="vertical">
          <Form.Item
            label="제목"
            name="title"
            rules={[{ required: true, message: '제목을 입력하세요.' }]}
          >
            <Input placeholder="제목을 입력하세요" />
          </Form.Item>
          <Form.Item
            label="카테고리"
            name="category"
            rules={[{ required: true, message: '카테고리를 선택하세요.' }]}
            extra={categories.length ? '등록된 카테고리 중에서 선택하세요.' : '사용 가능한 카테고리가 없습니다.'}
          >
            <Radio.Group style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {categories.map((cat) => (
                <Radio.Button key={cat} value={cat}>
                  {cat}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="내용"
            name="content"
            rules={[{ required: true, message: '내용을 입력하세요.' }]}
          >
            <Input.TextArea placeholder="내용을 입력하세요" rows={6} />
          </Form.Item>
          <Form.Item
            label="태그"
            name="tags"
            extra="콤마(,)로 구분해 입력하면 목록에 #태그로 표시됩니다."
          >
            <Input placeholder="예: 시스템, 공지" />
          </Form.Item>
          <Form.Item label="첨부 파일">
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Space size={8} wrap>
                <Button
                  icon={<UploadOutlined />}
                  onClick={handleOpenFilePicker}
                  disabled={boardCategoryLoading && !attachmentGroupId}
                >
                  파일 선택
                </Button>
                {boardCategoryLoading && !attachmentGroupId && <Spin size="small" />}
                {!attachmentGroupId && !boardCategoryLoading && !boardFileCategory?.fileCategoryId && (
                  <Text type="secondary">BOARD 카테고리를 불러오는 중입니다. 관리자에게 문의해주세요.</Text>
                )}
              </Space>
              <input
                type="file"
                multiple
                ref={attachmentInputRef}
                style={{ display: 'none' }}
                onChange={handleAttachmentSelection}
              />
              {pendingAttachments.length > 0 && (
                <List
                  size="small"
                  bordered
                  header="업로드 예정 파일"
                  dataSource={pendingAttachments}
                  renderItem={(item) => (
                    <List.Item
                      key={item.uid}
                      actions={[
                        <Button
                          key="remove"
                          type="link"
                          size="small"
                          danger
                          onClick={() => removePendingAttachment(item.uid)}
                        >
                          제거
                        </Button>
                      ]}
                    >
                      <Space size={8}>
                        <FileOutlined />
                        <span>{item.file?.name}</span>
                        <Text type="secondary">{formatFileSize(item.file?.size)}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              )}
              {existingAttachments.length > 0 && (
                <List
                  size="small"
                  bordered
                  header="기존 첨부 파일"
                  dataSource={existingAttachments}
                  renderItem={(file) => {
                    const marked = attachmentsToDelete.has(file.fileId);
                    return (
                      <List.Item
                        key={file.fileId}
                        actions={[
                          <Button
                            key="toggle"
                            type="link"
                            size="small"
                            danger={marked}
                            onClick={() => toggleAttachmentDeletion(file.fileId)}
                          >
                            {marked ? '복원' : '삭제'}
                          </Button>
                        ]}
                      >
                        <Space size={8}>
                          <FileOutlined />
                          <span style={{ textDecoration: marked ? 'line-through' : undefined }}>{file.originalFileName}</span>
                          <Text type="secondary">{formatFileSize(file.fileSize)}</Text>
                        </Space>
                      </List.Item>
                    );
                  }}
                />
              )}
              {pendingAttachments.length === 0 && existingAttachments.length === 0 && (
                <Text type="secondary">선택된 첨부 파일이 없습니다.</Text>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default BoardPage;
